import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ListPagerProps {
  page: number;
  totalPages: number;
  /** Shown as "· 1,204 records", so the reader knows how much is behind the list. */
  totalCount: number;
  onChange: (page: number) => void;
  /** "records" by default; a screen may prefer "cheques" or "statements". */
  noun?: string;
}

/** How many numbered buttons the strip shows before it starts sliding. */
const WINDOW = 5;

/**
 * The pager under a list.
 *
 * Eleven banking screens carried this, identically, including the arithmetic that decides which
 * five page numbers to show. Arithmetic repeated eleven times is arithmetic that is wrong in at
 * least one of them eventually.
 *
 * Renders nothing when there is only one page: a pager offering to take you to page 1 of 1 is
 * furniture.
 */
export const ListPager = ({ page, totalPages, totalCount, onChange, noun = 'records' }: ListPagerProps) => {
  if (totalPages <= 1) return null;

  // The window slides with the current page but never runs past either end.
  const first = Math.max(1, Math.min(page - 2, totalPages - (WINDOW - 1)));
  const shown = Array.from({ length: Math.min(WINDOW, totalPages) }, (_, i) => first + i);

  const arrow = 'p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 '
    + 'disabled:cursor-not-allowed transition-colors';

  return (
    <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
      <p className="text-sm text-slate-500 dark:text-gray-400">
        Page {page} of {totalPages} · {totalCount.toLocaleString()} {noun}
      </p>

      <nav aria-label="Pagination" className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className={arrow}
        >
          <ChevronLeft size={16} />
        </button>

        {shown.map(n => (
          <button
            key={n}
            type="button"
            aria-label={`Page ${n}`}
            aria-current={n === page ? 'page' : undefined}
            onClick={() => onChange(n)}
            className={cn(
              'w-8 h-8 text-sm rounded transition-colors',
              n === page
                ? 'bg-teal-600 text-white font-semibold'
                : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300',
            )}
          >
            {n}
          </button>
        ))}

        <button
          type="button"
          aria-label="Next page"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className={arrow}
        >
          <ChevronRight size={16} />
        </button>
      </nav>
    </div>
  );
};
